import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Icon, type IconName } from "../icons/Icon";
import "../../styles/components/button.css";

export type ButtonVariant = "primary" | "default" | "ghost" | "danger" | "text";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** 前置图标（自建图标集名称） */
  icon?: IconName;
  /** 后置图标（如 chevron-down） */
  iconRight?: IconName;
  /** 加载中：显示转圈并自动禁用，防重复提交 */
  loading?: boolean;
  children?: ReactNode;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: "btn-primary",
  default: "",
  ghost: "btn-ghost",
  danger: "btn-danger",
  text: "btn-text",
};

/**
 * 按钮。约定：
 *   · 写操作统一用 variant="primary"，并给 loading 防重复提交；
 *   · 破坏性操作用 variant="danger" 且必须配二次确认（见 Popconfirm）；
 *   · 表格行内次要操作用 variant="text" 或 size="sm"，避免页面按钮过多过重。
 */
export function Button({
  variant = "default",
  size = "md",
  icon,
  iconRight,
  loading = false,
  disabled,
  className = "",
  children,
  ...rest
}: ButtonProps) {
  const classes = [
    "btn",
    VARIANT_CLASS[variant],
    size === "sm" ? "btn-sm" : "",
    !children && (icon || iconRight) ? "btn-icon-only" : "",
    loading ? "is-loading" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button type="button" className={classes} disabled={disabled || loading} {...rest}>
      {loading ? <span className="btn-spinner" aria-hidden="true" /> : icon ? <Icon name={icon} size={size === "sm" ? 14 : 15} /> : null}
      {children}
      {iconRight && !loading ? <Icon name={iconRight} size={size === "sm" ? 14 : 15} /> : null}
    </button>
  );
}

export default Button;
